/**
 * Normalise the expiry year to 4 digits ('30' -> '2030'); 4-digit values pass through.
 * @param {?string} year
 * @returns {string}
 */
const toFullYear = year => {
  if (!year) return ''
  return year.length === 2 ? `20${year}` : year
}

/**
 * Build the `/transaction/card/reencrypt` request from the Evervault <Card>
 * onChange payload. `number` and `cvc` stay Evervault-encrypted (`ev:...`);
 * no other card fields (bin, lastFour, brand) are sent.
 *
 * @param {Object} card `cardFormValues.card` from the Evervault <Card>
 * @returns {{ name: string, number: string, expiry: { month: string, year: string }, cvc: string }}
 */
export const buildReencryptPayload = card => ({
  name: card?.name || '',
  number: card?.number || '',
  expiry: {
    month: card?.expiry?.month || '',
    year: toFullYear(card?.expiry?.year),
  },
  cvc: card?.cvc || '',
})

/**
 * Whether the Evervault <Card> onChange payload contains real field errors.
 * The SDK sends `errors: null | { number?, cvc?, expiry? }`; an empty
 * container or keys with empty values are not errors. Arrays are handled too.
 *
 * @param {?(Object|Array)} errors `payload.errors` from the Evervault <Card>
 * @returns {boolean}
 */
export const hasCardErrors = errors => {
  if (!errors || typeof errors !== 'object') return false
  return Object.values(errors).some(Boolean)
}

/** Gift card BINs — same list as the manual form and the BE `GiftCard.Bins`. */
export const GIFT_CARD_BINS = ['677713', '533944']

/**
 * Whether the card is a gift card that the transaction prohibits.
 * Evervault's `acceptedBrands` has no gift card brand, so the Evervault form
 * checks the BIN instead (the manual form does the same with the PAN).
 *
 * @param {?string} bin card BIN (Evervault `card.bin` or `/reencrypt` cardBin)
 * @param {?string[]} prohibitedCardBrands from `/details`
 * @returns {boolean}
 */
export const isProhibitedGiftCard = (bin, prohibitedCardBrands) =>
  Boolean(
    bin &&
    prohibitedCardBrands?.includes('GiftCard') &&
    GIFT_CARD_BINS.some(giftCardBin => String(bin).startsWith(giftCardBin)),
  )

/**
 * A usable BIN for Cardinal `bin.process` / DDC and `authorise`: 6+ digits.
 * @param {?string} bin
 * @returns {boolean}
 */
export const isValidCardBin = bin => /^\d{6,}$/.test(bin ?? '')
